import React, { useState, useEffect, useMemo } from 'react';
import { useService } from '@Client/runner.hooks/useService';
import { SharedAngular } from '@Client/@types/sharedAngular';
import {
  Grid,
  GridColumn as Column,
  GridPageChangeEvent,
  GridSortChangeEvent
} from '@progress/kendo-react-grid';
import Dialog from '@mui/material/Dialog';
import TagGroupEditModal from './Modals/Edit/EditModal';
import TagGroupDeleteModal from './Modals/Delete/DeleteModal';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';
import ITagGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagGroupDetail';
import { EmptyGuid } from '@Shared.Angular/@types/guid';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  orderBy,
  process,
  SortDescriptor
} from '@progress/kendo-data-query';
import { PagerTargetEvent } from '@progress/kendo-react-data-tools';
import { CustomPager } from '@Client/runner.shared/modal/Pager/CustomPager';
import ITagDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagDetail';
import SetupTagGroupsActions from './Actions/SetupTagGroupsActions';
import AutoCompleteFilter from './Filters/AutoCompleteFilter/AutoCompleteFilter';
import Chip from '../../../../../Flowingly.Shared.Angular/src/flowingly.components/Chip/Chip';

type PageState = {
  skip: number;
  take: number;
};

const emptyTag = {
  id: EmptyGuid,
  name: '',
  description: '',
  color: '',
  tags: []
};

const SetupTagGroups = () => {
  const tagGroupApiService =
    useService<SharedAngular.TagGroupApiService>('tagGroupApiService');
  const permissionsService =
    useService<SharedAngular.PermissionsService>('permissionsService');
  const appInsightsService =
    useService<SharedAngular.AppInsightsService>('appInsightsService');
  const pubSubService =
    useService<SharedAngular.PubSubService>('pubsubService');

  const [sort, setSort] = React.useState<SortDescriptor[]>([]);
  const [filter, setFilter] = useState<CompositeFilterDescriptor>(null);
  const [page, setPage] = React.useState<PageState>({ skip: 0, take: 25 });
  const [tagGroups, setTagGroups] = useState<ITagGroupDetail[]>([]);
  const [selectedTagGroup, setSelectedTagGroup] =
    useState<ITagGroupDetail>(emptyTag);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [hasTagInUse, setHasTagInUse] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const hasUpdatePermission = permissionsService.currentUserHasPermission(
    FlowinglyPermissions.SETUP_TAG_UPDATE
  );

  useEffect(() => {
    refreshData();

    const subscriberId = 'setup-tag-groups';
    pubSubService.subscribe(
      'SETUP_TAGGROUP_ADD_CLICKED',
      OpenAddTagGroupModal,
      subscriberId
    );
    pubSubService.subscribeLate(
      'TAG_GROUP_CREATED',
      getUpdatedTagGroups,
      subscriberId
    );
    pubSubService.subscribeLate(
      'TAG_GROUP_UPDATED',
      getUpdatedTagGroups,
      subscriberId
    );
    pubSubService.subscribeLate(
      'TAG_GROUP_DELETED',
      getUpdatedTagGroups,
      subscriberId
    );
    return () => {
      pubSubService.unsubscribeAll(subscriberId);
    };
  }, []);

  const getUpdatedTagGroups = () => {
    tagGroupApiService.getAll().then((newTagGroups) => {
      setTagGroups(newTagGroups);
    });
  };

  const refreshData = async (ignoreCache = false) => {
    appInsightsService.startEventTimer('setupTaggroupsLoaded');
    const response = await tagGroupApiService.getAll(ignoreCache);
    setTagGroups(response);
  };

  const customFilter = (
    data: ITagGroupDetail[],
    filter: CompositeFilterDescriptor
  ) => {
    if (!filter || !filter.filters.length) return data;

    const validFilters = filter.filters.filter((f) => {
      const filterItem = f as FilterDescriptor;
      return filterItem.value && filterItem.value.toString().trim() !== '';
    });

    if (!validFilters.length) return data;

    return data.filter((item) => {
      return validFilters.every((f) => {
        const filterItem = f as FilterDescriptor;

        if (filterItem.field === 'tags') {
          return item.tags.some((tag) => tag.name.includes(filterItem.value));
        } else if (filterItem.field === 'name') {
          return item.name
            .toLowerCase()
            .includes(filterItem.value.toString().toLowerCase());
        }

        return true;
      });
    });
  };

  const data = process(orderBy(customFilter(tagGroups, filter), sort), {
    skip: page.skip,
    take: page.take
  });

  const handleSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort);
  };

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === 'All' ? tagGroups.length : event.page.take;
    setPage({
      ...event.page,
      take
    });
  };

  const OpenAddTagGroupModal = () => {
    setSelectedTagGroup(emptyTag);
    setModalTitle('Add Tag Group');
    setEditOpen(true);
  };

  const editTagGroup = async (selectedTagGroup) => {
    const tagGroup = await tagGroupApiService.getById(selectedTagGroup.id);
    setSelectedTagGroup(tagGroup);
    setModalTitle('Edit Tag Group');
    setEditOpen(true);
  };

  const deleteTagGroup = async (selectedTagGroup) => {
    appInsightsService.startEventTimer('setupTaggroupsDeleteWarning');
    setSelectedTagGroup(selectedTagGroup);
    const response = await tagGroupApiService.getDeleteWarnings(
      selectedTagGroup.id
    );
    setHasTagInUse(response.hasTagInUse);
    setDeleteOpen(true);
  };

  const handleSaveSuccess = async () => {
    await refreshData(true);
    closeEditModal();
  };

  const closeEditModal = () => {
    setEditOpen(false);
  };

  const handleDeleteSuccess = async () => {
    await refreshData(true);
    setDeleteOpen(false);
  };

  const ActionCell = (props) => {
    return (
      <SetupTagGroupsActions
        dataItem={props.dataItem}
        editTagGroup={editTagGroup}
        deleteTagGroup={deleteTagGroup}
        hasUpdatePermission={hasUpdatePermission}
      />
    );
  };

  const TagsCell = (props: {
    dataItem: { tags: ITagDetail[]; color: string };
  }) => {
    const tags = props.dataItem.tags || [];
    return (
      <td>
        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
          {tags.map((tag) => (
            <Chip key={tag.id} name={tag.name} color={props.dataItem.color} />
          ))}
        </div>
      </td>
    );
  };

  const NameFilterCell = (props) => {
    return (
      <AutoCompleteFilter
        {...props}
        data={Array.from(new Set(tagGroups.map((p) => p.name)))}
        placeholder="Search by Tag Group"
      />
    );
  };

  const TagFilterCell = (props) => {
    return (
      <AutoCompleteFilter
        {...props}
        data={Array.from(
          new Set(tagGroups.flatMap((p) => p.tags.map((tag) => tag.name)))
        )}
        placeholder="Search by Tag"
      />
    );
  };

  const NameFilterCellMemo = useMemo(() => NameFilterCell, [tagGroups]);
  const TagFilterCellMemo = useMemo(() => TagFilterCell, [tagGroups]);

  return (
    <div className="setup-tag-groups-outer-container">
      <link
        href="https://kendo.cdn.telerik.com/themes/7.0.1/material/material-main.css"
        rel="stylesheet"
        type="text/css"
      />
      <div className="setup-tag-groups-container">
        <Grid
          className="setup-tag-groups-container"
          dataItemKey={'id'}
          data={data}
          sortable={true}
          sort={sort}
          onSortChange={handleSortChange}
          filterable={true}
          filter={filter}
          onFilterChange={(e) => {
            setFilter(e.filter);
          }}
          skip={page.skip}
          take={page.take}
          total={tagGroups.length}
          pageable={true}
          pager={CustomPager}
          onPageChange={pageChange}
        >
          <Column
            field="name"
            title="Tag Group"
            width="250"
            filterCell={NameFilterCellMemo}
          />
          <Column
            field="description"
            title="Description"
            width="350"
            filterable={false}
          />
          <Column field="owner" title="Owner" width="200" filterable={false} />
          <Column
            field="numberOfFlowModelsUsing"
            title="Flow Models"
            width="200"
            filterable={false}
          />
          <Column
            field="tags"
            title="Tags"
            cell={TagsCell}
            filterCell={TagFilterCellMemo}
          />
          <Column
            title="Actions"
            width="100"
            sortable={false}
            filterable={false}
            cell={ActionCell}
          />
        </Grid>
      </div>

      <Dialog
        open={editOpen}
        PaperProps={{ className: 'setup-tag-groups-dialog' }}
      >
        <TagGroupEditModal
          modalTitle={modalTitle}
          id={selectedTagGroup.id}
          name={selectedTagGroup.name}
          description={selectedTagGroup.description}
          color={selectedTagGroup.color}
          tags={selectedTagGroup.tags}
          saveSuccess={() => handleSaveSuccess()}
          closeModal={() => closeEditModal()}
        />
      </Dialog>

      <Dialog
        open={deleteOpen}
        PaperProps={{ className: 'setup-tag-groups-dialog' }}
      >
        <TagGroupDeleteModal
          id={selectedTagGroup.id}
          name={selectedTagGroup.name}
          hasTagInUse={hasTagInUse}
          deleteSuccess={() => handleDeleteSuccess()}
          closeModal={() => setDeleteOpen(false)}
        />
      </Dialog>
    </div>
  );
};

export default SetupTagGroups;
